import { Component, forwardRef, OnInit, inject } from '@angular/core';
import { WidgetComponent } from './widget.component';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { blurOnKeyDown } from '../common/utils/keypress.utils';
import _ from 'lodash';
import { PropertyDisplayType } from './property-display-type.enum';
import { PropertyWidgetContext } from './property-widget.context';
import { PropertyWidgetContextType } from './property-widget-context-type.enum';

@Component({
    selector: 'adm4-simple-textarea-property',
    template: `
    <div [formGroup]='group' class='input-field-container'>
      @switch (type) {
        @case (displayType.MULTI) {
          <textarea
            [placeholder]='multiLinePlaceholder' [rows]='defaultNumberOfRows'
            class='admn4-textarea-input form-control textarea-property'
            [readonly]='readOnly'
            [formControlName]='widgetProperty.propertyKey'
            #textArea
            (focus)='onSelect($event, textArea)'
            (blur)='trimWhiteSpace($event)'
          ></textarea>
        }
        @case (displayType.SINGLE) {
          <input
            [placeholder]='defaultText'
            class='admn4-text-input form-control textfield-property'
            [readonly]='readOnly'
            [formControlName]='widgetProperty.propertyKey'
            (keydown.enter)='onEnterPressed($event)'
            (focus)='onSelect($event, this)'
            (blur)='trimWhiteSpace($event)'
            />
        }
        @case (displayType.SECRET_WARNING) {
          <span>
            Please set a variable for this property. To define the value, edit the inventory and insert a secret.
          </span>
        }
      }
      @if (type === displayType.SINGLE || type === displayType.MULTI) {
        <button type="button" class="adm4-button-inline" (click)="copyValue()">
          <mat-icon class="size-16">content_copy</mat-icon>
        </button>
      }
    </div>
    `,
    styleUrls: ['widget.component.scss', 'simple-textarea-property.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SimpleTextareaPropertyComponent),
            multi: true
        }],
    standalone: false
})

export class SimpleTextareaPropertyComponent extends WidgetComponent implements OnInit {
  private pwc = inject(PropertyWidgetContext);


  private readonly PLACEHOLDER_TEXT = 'Please enter one value per line...';
  private readonly DEFAULT_ROW_NUMBER = 2;

  private readonly EXTENDED_ROW_NUMBER = 3;
  public readonly displayType = PropertyDisplayType;

  override isMultiValue() {
    return true;
  }

  get type(): PropertyDisplayType {
    if (this.isSecret && this.isValueEmpty() && this.isPatternEditorScreen()) {
      return PropertyDisplayType.SECRET_WARNING;
    }
    if (this.isPropertyMultiValue()) {
      return PropertyDisplayType.MULTI;
    }
    return PropertyDisplayType.SINGLE;
  }

  isPropertyMultiValue(): boolean {
    return this.maxAllowed > 1;
  }

  /**
   * Cannot call blurOnEnterDown directly from template
   * @param event
   */
  onEnterPressed(event): void {
    blurOnKeyDown(event);
  }

  copyValue(): void {
    let value: string;
    if (this.isValueEmpty()) {
      if (_.isArray(this.defaultValue)) {
        value = this.defaultValue.join('\n');
      } else if (_.isNil(this.defaultValue)) {
        value = '';
      } else {
        value = String(this.defaultValue);
      }
    } else {
      value = this.group.controls[this.widgetProperty.propertyKey].value;
    }
    navigator.clipboard.writeText(value).then(() => {
      // nothing to do
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  }

  get multiLinePlaceholder(): string {
    if (_.isArray(this.defaultValue)) {
      return `default:\n${this.defaultValue.join('\n')}`;
    } else if (_.isNil(this.defaultValue)) {
      return this.PLACEHOLDER_TEXT;
    }
    return this.defaultText;
  }

  get defaultNumberOfRows(): number {
    if (_.isArray(this.defaultValue) && this.defaultValue.length > 1) {
      return this.EXTENDED_ROW_NUMBER;
    }
    return this.DEFAULT_ROW_NUMBER;
  }

  isPatternEditorScreen() {
    return this.pwc.type === PropertyWidgetContextType.PATTERN_EDITOR;
  }

  trimWhiteSpace(event) {
    if (this.readOnly) {
      return;
    }
    this.group.controls[this.widgetProperty.propertyKey].setValue(_.trim(event.target.value));
  }
}
